import { CircleInfoIcon, PlusIcon } from "@xola/icons";
import { Tooltip } from "@xola/ui-kit";
import { SecondaryButton } from "common";
import { useFormikContext } from "formik";
import React, { useState } from "react";
import { DropResult } from "react-beautiful-dnd";
import { VariantOption } from "types";
import { AddVariantOptionModal } from "./AddVariantOptionModal";
import { DeleteVariantOptionModal } from "./DeleteVariantOptionModal";
import { VariantOptionsDndTable } from "./VariantOptionsDndTable";
import { useModal } from "@/hooks/useVisibility";
import { reorder } from "@/utils/reorder";

interface AddVariantGroupFormValues {
    name: string;
    options: VariantOption[];
}

export const VariantOptionsSection = () => {
    const { values, setFieldValue, submitCount } = useFormikContext<AddVariantGroupFormValues>();
    const addOptionModal = useModal();
    const deleteOptionModal = useModal();
    const [editingOption, setEditingOption] = useState<VariantOption | undefined>();
    const [editingOptionIndex, setEditingOptionIndex] = useState<number | undefined>();
    const [deletingOption, setDeletingOption] = useState<VariantOption | undefined>();
    const [deletingOptionIndex, setDeletingOptionIndex] = useState<number | undefined>();

    const handleAddOption = (option: VariantOption | VariantOption[]) => {
        if (editingOptionIndex !== undefined && !Array.isArray(option)) {
            const updatedOptions = [...values.options];
            updatedOptions[editingOptionIndex] = option;
            setFieldValue("options", updatedOptions);
        } else if (Array.isArray(option)) {
            setFieldValue("options", [...values.options, ...option]);
        } else {
            setFieldValue("options", [...values.options, option]);
        }
        setEditingOption(undefined);
        setEditingOptionIndex(undefined);
    };

    const handleEditOption = (option: VariantOption, index: number) => {
        setEditingOption(option);
        setEditingOptionIndex(index);
        addOptionModal.show();
    };

    const handleDeleteOption = (optionIndex: number) => {
        const updatedOptions = values.options.filter((_, index) => index !== optionIndex);
        setFieldValue("options", updatedOptions);
    };

    const handleOpenDeleteModal = (option: VariantOption, index: number) => {
        setDeletingOption(option);
        setDeletingOptionIndex(index);
        deleteOptionModal.show();
    };

    const handleConfirmDelete = () => {
        if (deletingOptionIndex !== undefined) {
            handleDeleteOption(deletingOptionIndex);
            setDeletingOption(undefined);
            setDeletingOptionIndex(undefined);
            deleteOptionModal.hide();
        }
    };

    const handleDragEnd = (result: DropResult) => {
        if (!result.destination) {
            return;
        }
        const reorderedOptions = reorder(values.options, result.source.index, result.destination.index);
        setFieldValue("options", reorderedOptions);
    };

    const handleOpenAddModal = () => {
        setEditingOption(undefined);
        setEditingOptionIndex(undefined);
        addOptionModal.show();
    };

    return (
        <div className="space-y-3">
            <div className="flex items-center justify-between">
                <div className="flex items-center text-sm font-bold">
                    <span>Options</span>
                    <Tooltip content='The options of this variant that can be assigned to products for this variant (e.g. "Small" or "Large")'>
                        <CircleInfoIcon className="ml-1" />
                    </Tooltip>
                </div>
                <SecondaryButton size="small" type="button" onClick={handleOpenAddModal}>
                    <PlusIcon size="small" className="mr-1" />
                    Add Option
                </SecondaryButton>
            </div>

            {values.options.length > 0 ? (
                <VariantOptionsDndTable
                    options={values.options}
                    onDragEnd={handleDragEnd}
                    onEditOption={handleEditOption}
                    onDeleteOption={handleOpenDeleteModal}
                />
            ) : (
                <p className="text-sm text-secondary">
                    No options added yet. Click &ldquo;Add Option&rdquo; to create one.
                </p>
            )}

            {submitCount > 0 && values.options.length === 0 && (
                <p className="text-sm text-danger">At least one option is required</p>
            )}

            <AddVariantOptionModal
                isOpen={addOptionModal.isVisible}
                currentOption={editingOption}
                existingOptions={values.options}
                variantName={values.name}
                onClose={addOptionModal.hide}
                onAdd={handleAddOption}
            />

            <DeleteVariantOptionModal
                isOpen={deleteOptionModal.isVisible}
                option={deletingOption}
                onClose={deleteOptionModal.hide}
                onConfirm={handleConfirmDelete}
            />
        </div>
    );
};
